import React, { useState, useEffect } from 'react';
import { FaBrain, FaHeart, FaLightbulb, FaBriefcase, FaChevronDown, FaChevronUp, FaCheckCircle, FaFileAlt } from 'react-icons/fa';
import { 
  getAllTests, 
  startTest, 
  getNextQuestion, 
  submitAnswer, 
  getTestResults,
  getTestReport
} from '../lib/api/psychtestApi';
import TestResultsModal from '../components/TestResultsModal';
import TestReportModal from '../components/TestReportModal';
import QuizModal from '../components/QuizModal';
import Lottie from 'lottie-react';

// Icon mapping based on test id or name
const getTestIcon = (testId, testName = '') => {
  // Default mapping based on ID
  const iconMap = {
    1: <FaBrain className="text-pink-500" />,
    2: <FaHeart className="text-red-500" />,
    3: <FaLightbulb className="text-yellow-500" />,
    4: <FaBriefcase className="text-brown-500" />,
    5: <FaBrain className="text-blue-500" />
  };
  
  // If we have an icon for this ID, return it
  if (iconMap[testId]) {
    return iconMap[testId];
  }
  
  // Fallback logic based on test name
  const name = testName.toLowerCase();
  if (name.includes('personality') || name.includes('cognitive')) {
    return <FaBrain className="text-pink-500" />;
  } else if (name.includes('emotion') || name.includes('social')) {
    return <FaHeart className="text-red-500" />;
  } else if (name.includes('career') || name.includes('professional')) {
    return <FaBriefcase className="text-gray-500" />;
  } else {
    return <FaLightbulb className="text-yellow-500" />;
  }
};

// Map API test status to our UI status
const mapTestStatus = (apiStatus) => {
  const statusMap = {
    0: 'locked',    // LOCKED
    1: 'available', // AVAILABLE
    2: 'available', // IN_PROGRESS (still available, but with progress)
    3: 'completed'  // COMPLETED
  };
  return statusMap[apiStatus] || 'available';
};

// Helper to ensure options is an array
const ensureOptionsArray = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === 'string') {
    try {
      // Try to parse if it's a JSON string
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [options];
    } catch (e) {
      // If parsing fails, treat as a single option
      return [options];
    }
  }
  // For objects or other types, return as a single-item array
  return [options];
};

const AssessmentsPage = () => {
  const [activeFilter, setActiveFilter] = useState('available');
  const [openQuizId, setOpenQuizId] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [testReport, setTestReport] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(1);
  const [animationData, setAnimationData] = useState(null);
  
  useEffect(() => {
    // Fetch the Lottie animation
    fetch('https://db.pokketcoach.com/storage/v1/object/public/public-animations/lottie-animations/pokketsleeping.json')
      .then(response => response.json())
      .then(data => {
        setAnimationData(data);
      })
      .catch(error => {
        console.error('Error loading Lottie animation:', error);
      });
  }, []);

  // Fetch all tests on component mount
  useEffect(() => {
    const fetchTests = async () => {
      setLoading(true);
      try {
        const { data, error } = await getAllTests();
        if (error) throw new Error('Failed to fetch tests');
        
        if (data) {
          // Transform API data to our UI format
          const transformedTests = data.map(test => ({
            id: test.testId,
            icon: getTestIcon(test.testId, test.testName),
            title: test.testName,
            description: test.description,
            explanation: test.explanation,
            status: mapTestStatus(test.status),
            progress: test.questionsAnswered / test.totalQuestions * 100 || 0,
            isLocked: test.isLocked,
            unlockRequirements: test.unlockRequirements,
            hasInProgressSession: test.hasInProgressSession,
            sessionId: test.sessionId,
            totalQuestions: test.totalQuestions,
            questionsAnswered: test.questionsAnswered,
            currentQuestion: null // Will be populated when a quiz is opened
          }));
          
          setTests(transformedTests);
          
          // Check if there are any available or unlocked tests
          const hasAvailableTests = transformedTests.some(test => 
            test.status === 'available' && !test.isLocked
          );
          
          // If no available tests, set filter to "completed"
          if (!hasAvailableTests && transformedTests.some(test => test.status === 'completed')) {
            setActiveFilter('completed');
          }
        }
      } catch (err) {
        console.error('Error fetching tests:', err);
        setError('Failed to load tests. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTests();
  }, []);
  
  // Handle starting a test
  const handleStartTest = async (testId) => {
    try {
      const { data, error } = await startTest(testId);
      if (error) throw new Error('Failed to start test');
      
      if (data && data.sessionId) {
        // Store the session ID
        setCurrentSessionId(data.sessionId);
        
        // Update the specific test's status in our state
        setTests(prevTests => 
          prevTests.map(test => 
            test.id === testId 
              ? { 
                  ...test, 
                  sessionId: data.sessionId,
                  hasInProgressSession: true,
                  status: 'available' 
                } 
              : test
          )
        );
        
        // Set the active quiz
        const test = tests.find(t => t.id === testId);
        setActiveQuiz({
          ...test,
          sessionId: data.sessionId,
          totalQuestions: test.totalQuestions
        });
        
        // Reset question number
        setCurrentQuestionNumber(1);
        
        // Fetch the first question and open the modal
        await fetchNextQuestion(data.sessionId, true);
      }
    } catch (err) {
      console.error('Error starting test:', err);
      setError('Failed to start test. Please try again later.');
    }
  };
  
  // Fetch the next question for a session
  const fetchNextQuestion = async (sessionId, shouldOpenModal = false) => {
    try {
      const { data, error } = await getNextQuestion(sessionId);
      if (error) throw new Error('Failed to fetch question');
      
      if (data) {
        if (data.completed) {
          // Test is completed, fetch report instead of results
          handleViewReport(sessionId);
          
          // Close quiz modal
          setIsQuizModalOpen(false);
          
          // Update test status
          setTests(prevTests => 
            prevTests.map(test => 
              test.sessionId === sessionId 
                ? { 
                    ...test, 
                    status: 'completed',
                    progress: 100 
                  } 
                : test
            )
          );
          
          // Reset active quiz
          setActiveQuiz(null);
          
        } else if (data.question) {
          // Update current question and ensure options is an array
          const questionOptions = ensureOptionsArray(data.question.options);
          setCurrentQuestion({
            ...data.question,
            options: questionOptions
          });
          
          // Open quiz modal if requested
          if (shouldOpenModal) {
            setIsQuizModalOpen(true);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching question:', err);
      setError('Failed to fetch question. Please try again later.');
    }
  };
  
  // Handle submitting an answer
  const handleSubmitAnswer = async (sessionId, questionId, answerValue) => {
    if (answerValue === null) {
      // No answer selected
      return;
    }
    
    try {
      const { error } = await submitAnswer(sessionId, questionId, answerValue);
      if (error) throw new Error('Failed to submit answer');
      
      // Update question count and progress
      setTests(prevTests => 
        prevTests.map(test => 
          test.sessionId === sessionId 
            ? { 
                ...test, 
                questionsAnswered: Math.min(test.questionsAnswered + 1, test.totalQuestions),
                progress: Math.min(((test.questionsAnswered + 1) / test.totalQuestions) * 100, 100)
              } 
            : test
        )
      );
      
      // Increment question number for display purposes
      setCurrentQuestionNumber(prevNum => prevNum + 1);
      
      // Reset selected answer
      setSelectedAnswer(null);
      
      // Fetch next question
      await fetchNextQuestion(sessionId, true);
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Failed to submit answer. Please try again later.');
    }
  };
  
  // Handle resuming a test
  const handleResumeTest = async (testId, sessionId) => {
    setCurrentSessionId(sessionId);
    
    // Set the active quiz
    const test = tests.find(t => t.id === testId);
    setActiveQuiz({
      ...test,
      sessionId: sessionId,
      totalQuestions: test.totalQuestions
    });
    
    // Set current question number based on questions already answered
    setCurrentQuestionNumber(test.questionsAnswered + 1);
    
    // Fetch the next question and open the modal
    await fetchNextQuestion(sessionId, true);
  };
  
  // Handle closing the quiz modal
  const handleCloseQuizModal = () => {
    setIsQuizModalOpen(false);
  };
  
  // Handle viewing results
  const handleViewResults = async (sessionId) => {
    try {
      const { data, error } = await getTestResults(sessionId);
      if (error) throw new Error('Failed to fetch results');
      
      if (data) {
        setTestResults(data);
        setIsResultsModalOpen(true);
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError('Failed to fetch results. Please try again later.');
    }
  };
  
  // Handle viewing test report
  const handleViewReport = async (sessionId) => {
    try {
      console.log('View report clicked for session ID:', sessionId);
      console.log('Test data:', tests.find(t => t.sessionId === sessionId));
      
      // Set initial loading state - show the modal with a loading indicator
      setTestReport({
        isLoading: true,
        reportContent: null,
        sessionId: sessionId,
        testName: tests.find(t => t.sessionId === sessionId)?.title || 'Test Report'
      });
      setIsReportModalOpen(true);
      
      const { data, error } = await getTestReport(sessionId);
      if (error) {
        console.error('Error details:', error);
        throw new Error('Failed to fetch report');
      }
      
      console.log('Report data received:', data);
      
      if (data) {
        const testName = tests.find(t => t.sessionId === sessionId)?.title || 'Test Report';
        console.log('Setting report with test name:', testName);
        
        // Check if the report has content
        const hasContent = data.reportContent || data.content || data.report || data.text || 
                          (data.data && (data.data.reportContent || data.data.content || data.data.text));
        
        if (!hasContent) {
          // Report is still processing
          setTestReport({
            isProcessing: true,
            sessionId: sessionId,
            testName: testName
          });
        } else {
          // Report is ready
          setTestReport({
            ...data,
            isLoading: false,
            isProcessing: false,
            sessionId: sessionId,
            testName: testName
          });
        }
      } else {
        // No data received - report is likely still processing
        console.log('No data received from API');
        setTestReport({
          isProcessing: true,
          sessionId: sessionId, 
          testName: tests.find(t => t.sessionId === sessionId)?.title || 'Test Report'
        });
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to fetch report. Please try again later.');
      // Close the modal if there was an error
      setIsReportModalOpen(false);
    }
  };
  
  // Close results modal
  const closeResultsModal = () => {
    setIsResultsModalOpen(false);
  };
  
  // Close report modal
  const closeReportModal = () => {
    setIsReportModalOpen(false);
  };
  
  const toggleQuiz = (id) => {
    if (openQuizId === id) {
      setOpenQuizId(null);
    } else {
      setOpenQuizId(id);
      
      // If the quiz has an in-progress session, fetch the current question
      const test = tests.find(t => t.id === id);
      if (test && test.hasInProgressSession && test.sessionId) {
        setCurrentSessionId(test.sessionId);
        fetchNextQuestion(test.sessionId);
      }
    }
  };
  
  // Filter quizzes based on the active filter
  // For available, we'll include both available and locked tests
  const filteredTests = activeFilter === 'available'
    ? [...tests.filter(test => test.status === 'available'), ...tests.filter(test => test.status === 'locked')]
    : tests.filter(test => test.status === activeFilter);
  
  if (loading) {
    return (
      <div className="assessments-page container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className="text-lg text-gray-600">Loading assessments...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="assessments-page container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <p className="text-lg text-red-600">{error}</p>
            <button 
              className="mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-600"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="assessments-page container mx-auto px-4 py-8 max-w-4xl">
      <div className="assessments-page__header text-center mb-6">
        <h1 className="assessments-page__title text-4xl font-bold text-gray-800 mb-10">
          Pokket's Psychological Profiling Tools
        </h1>
        <p className="assessments-page__subtitle text-lg text-gray-600 mb-6">
          Your mindset has stats - let's measure them. Each quiz unlocks insight into your competitive mindset, tilting patterns, and resilience. Pokket provides an in-depth analysis of your scores, helping you track your progress and performance over time.
        </p>
        
        {/* Lottie Animation */}
        <div className="assessments-page__animation w-60 h-60 mx-auto mb-10">
          {animationData && (
            <Lottie 
              animationData={animationData} 
              loop={true}
              autoplay={true}
            />
          )}
        </div>
      </div>
      
      {/* Filter tabs */}
      <div className="assessments-page__filters flex items-center mb-6">
        <h2 className="text-2xl font-semibold mr-auto">Psychometric Quizzes</h2>
        <div className="assessments-page__tabs flex space-x-2">
          <button 
            className={`assessments-page__tab-button px-4 py-2 rounded-full text-sm font-medium ${activeFilter === 'available' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setActiveFilter('available')}
          >
            Available
          </button>
          <button 
            className={`assessments-page__tab-button px-4 py-2 rounded-full text-sm font-medium ${activeFilter === 'completed' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setActiveFilter('completed')}
          >
            Completed
          </button>
        </div>
      </div>
      
      {/* Tests list */}
      <div className="assessments-page__quizzes space-y-6">
        {filteredTests.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">No tests found in this category.</p>
          </div>
        ) : (
          filteredTests.map(test => {
            const isOpen = openQuizId === test.id;
            return (
              <div 
                key={test.id} 
                className={`assessments-page__quiz-card bg-white rounded-xl card-shadow overflow-hidden transition-all duration-300 ease-in-out ${test.status === 'locked' ? 'opacity-70' : ''}`}
              >
                {/* Clickable Header Area */}
                <div 
                  className="assessments-page__quiz-header p-6 cursor-pointer group"
                  onClick={() => toggleQuiz(test.id)}
                >
                  <div className="flex items-start">
                    <div className="assessments-page__quiz-icon flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                      {test.icon}
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="assessments-page__quiz-title text-xl font-semibold">{test.title}</h3>
                        <div className="flex items-center">
                          {/* Status Indicator Logic */} 
                          {test.status === 'available' && (
                            <span className="assessments-page__quiz-status text-green-500 flex items-center text-sm mr-3">
                              <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Available
                            </span>
                          )}
                          {test.status === 'completed' && (
                            <span className="assessments-page__quiz-status text-primary flex items-center text-sm mr-3">
                              <FaCheckCircle className="w-4 h-4 mr-1" />
                              Completed
                            </span>
                          )}
                          {test.status === 'locked' && (
                            <span className="assessments-page__quiz-status text-gray-500 flex items-center text-sm mr-3">
                              <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 116 0z" clipRule="evenodd" />
                              </svg>
                              Locked
                            </span>
                          )}
                          {/* Chevron Indicator */}
                          <span className="text-gray-400 group-hover:text-primary transition-colors">
                            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                          </span>
                        </div>
                      </div>
                      <p className="assessments-page__quiz-description text-gray-600">
                        {test.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Collapsible Content Area */}
                <div 
                  className={`assessments-page__quiz-content overflow-hidden transition-max-height duration-500 ease-in-out ${isOpen ? 'max-h-screen' : 'max-h-0'}`}
                >
                  <div className="px-6 pb-6 pt-2">
                    {/* Locked Test Details */}
                    {test.status === 'locked' && test.unlockRequirements && (
                      <div className="assessments-page__unlock-requirements mt-4">
                        <h4 className="text-lg font-medium mb-3">Unlock Requirements:</h4>
                        <ul className="space-y-2 text-gray-600">
                          <li className="flex items-center">
                            <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3 text-gray-500">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            </span>
                            <span className={test.unlockRequirements.currentConversations >= test.unlockRequirements.minimumConversations ? 'text-blue-600' : ''}>
                              {test.unlockRequirements.currentConversations >= test.unlockRequirements.minimumConversations ? (
                                <>
                                  {test.unlockRequirements.minimumConversations} / {test.unlockRequirements.minimumConversations} conversations completed
                                  <svg className="w-4 h-4 ml-2 text-green-500 inline" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </>
                              ) : (
                                <>
                                  {test.unlockRequirements.currentConversations} / {test.unlockRequirements.minimumConversations} conversations completed
                                </>
                              )}
                            </span>
                          </li>
                          <li className="flex items-center">
                            <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3 text-gray-500">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                              </svg>
                            </span>
                            <span className={test.unlockRequirements.currentAdviceReceived >= test.unlockRequirements.minimumAdviceReceived ? 'text-blue-600' : ''}>
                              {test.unlockRequirements.currentAdviceReceived >= test.unlockRequirements.minimumAdviceReceived ? (
                                <>
                                  {test.unlockRequirements.minimumAdviceReceived} / {test.unlockRequirements.minimumAdviceReceived} advice sessions received
                                  <svg className="w-4 h-4 ml-2 text-green-500 inline" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </>
                              ) : (
                                <>
                                  {test.unlockRequirements.currentAdviceReceived} / {test.unlockRequirements.minimumAdviceReceived} advice sessions received
                                </>
                              )}
                            </span>
                          </li>
                        </ul>
                      </div>
                    )}
                    
                    {/* Test explanation section */}
                    {test.status !== 'locked' && test.explanation && (
                      <div className="mt-4 text-gray-600">
                        <h4 className="text-lg font-medium mb-2">About this test:</h4>
                        <p>{test.explanation}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions Area */}
                  <div className="assessments-page__quiz-actions bg-gray-50 px-6 py-4 border-t border-gray-100">
                    {/* Start button for available tests without progress */}
                    {test.status === 'available' && test.progress === 0 && (
                      <div className="flex justify-center w-full">
                        <button 
                          className="assessments-page__start-button bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-600"
                          onClick={() => handleStartTest(test.id)}
                        >
                          Start Quiz
                        </button>
                      </div>
                    )}
                    
                    {/* Resume button for available tests with progress */}
                    {test.status === 'available' && test.progress > 0 && (
                      <div className="flex justify-center w-full">
                        <button 
                          className="assessments-page__resume-button bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-600"
                          onClick={() => handleResumeTest(test.id, test.sessionId)}
                        >
                          Resume Quiz
                        </button>
                      </div>
                    )}
                    
                    {/* View Results and Report buttons for completed tests */}
                    {test.status === 'completed' && (
                      <div className="flex justify-center w-full">
                        <button 
                          className="assessments-page__report-button bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-600 flex items-center"
                          onClick={() => handleViewReport(test.sessionId)}
                        >
                          <FaFileAlt className="mr-2" />
                          <span>View Report</span>
                        </button>
                      </div>
                    )}
                    
                    {/* Unlock button for locked tests */}
                    {test.status === 'locked' && (
                      <div className="flex justify-center w-full">
                        <button 
                          className="assessments-page__unlock-button border border-gray-300 text-gray-600 px-6 py-2 rounded-lg hover:bg-gray-200"
                          disabled={true}
                        >
                          Talk to Pokket to Unlock
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Results Modal */}
      <TestResultsModal 
        isOpen={isResultsModalOpen}
        onClose={closeResultsModal}
        results={testResults}
      />
      
      {/* Report Modal */}
      <TestReportModal
        isOpen={isReportModalOpen}
        onClose={closeReportModal}
        report={testReport}
      />
      
      {/* Quiz Modal */}
      <QuizModal
        isOpen={isQuizModalOpen}
        onClose={handleCloseQuizModal}
        sessionId={activeQuiz?.sessionId}
        currentQuestion={currentQuestion}
        totalQuestions={activeQuiz?.totalQuestions || 0}
        questionNumber={currentQuestionNumber}
        onSubmitAnswer={handleSubmitAnswer}
      />
    </div>
  );
};

export default AssessmentsPage; 